import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { yellow } from '@ant-design/colors';
import {
  Spin,
  Row,
  Button,
  Skeleton,
  Upload,
  Popover,
  notification,
  Avatar
} from 'antd';
import PageHeader from '../../components/PageHeader/PageHeader';
import { EditOutlined, WarningOutlined } from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import ContentCustom from '../../components/ContentCustom/ContentCustom';
import DescriptionList from '../../components/DescriptionList/DescriptionList';
import useInsurerList from './listContent';
import { uploadLogo } from '../../utils/resizeUpload';

const MyBrokerFirm = () => {
  const { t } = useTranslation();
  const { dispatchAPI, user, setUser } = useAuthContext();
  const { infoBrokerFirm, infoOwner, infoGroup } = useInsurerList(user?.insurer || {});
  const { message: customMessage } = useErrorMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [isLogoLoading, setIsLogoLoading] = useState(false);
  const [employees, setEmployees] = useState();
  const [imageUrl, setImageUrl] = useState();
  const [hover, setHover] = useState(false);

  const isReductScreen = useMediaQuery({ query: '(max-width: 1100px)' });
  const isXSScreen = useMediaQuery({ query: '(max-width: 600px)' });

  const getEmployees = async (insurerId) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/employees?insurer=${insurerId}`
      });
      setEmployees(data);
    } catch (e) {
      if (e.response) customMessage(e.response.status);
    }
  };

  const getLogo = async (id) => {
    setIsLogoLoading(true);
    try {
      const result = await dispatchAPI('GET', {
        responseType: 'blob',
        url: `/insurers/avatar/${id}`
      });
      setImageUrl(URL.createObjectURL(result.data));
    } catch (e) {
      customMessage(e);
    }
    setIsLogoLoading(false);
  };

  const getUser = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `users/${user?._id}?populate=insurer`
      });
      setUser(data);
    } catch (e) {
      customMessage(e);
    }
  };

  useEffect(() => {
    if (user?.insurer?._id && !employees) {
      (async () => {
        await getEmployees(user.insurer._id);
        await getUser();
        setIsLoading(false);
      })();
    }
    if (user?.insurer?.logo) {
      (async () => {
        await getLogo(user.insurer.logo);
      })();
    }
    // eslint-disable-next-line
  }, [user?.insurer]);

  const beforeUpload = (file) => {
    setIsLogoLoading(true);
    uploadLogo(file, {
      fileName: file.name,
      mimeType: file.type,
      maxWidth: 180,
      maxHeight: 49,
      callback: async (newFile) => {
        try {
          const formData = new FormData();
          formData.append('logo', newFile);
          await dispatchAPI('PATCH', {
            url: `insurers/avatar/${user?.insurer?._id}`,
            body: formData
          });
          await getUser();
          // const { data } = await dispatchAPI('GET', {
          //   url: `users/${user?._id}?populate=insurer`
          // });
          notification.success({ message: 'Logo mis à jour avec succès' });
          // setUser(data);
        } catch (e) {
          customMessage(e);
        }
      }
    });
    setIsLogoLoading(false);
    return false;
  };

  return (
    <>
      <PageHeader
        title={user?.insurer?.name}
        extra={
          <Link
            to={{
              pathname: `/settings/edit/${user?.insurer?._id}`
            }}
          >
            <Button
              type="primary"
              style={{
                width: isReductScreen ? 35 : 'auto',
                padding: isReductScreen ? '4px 4px' : '4px 15px'
              }}
            >
              <span style={{ display: isReductScreen ? 'none' : 'inline' }}>
                {t('buttons.edit_insurer')}
              </span>
              <EditOutlined
                style={{
                  marginLeft: isReductScreen ? 0 : 8
                }}
              />
            </Button>
          </Link>
        }
      />

      <ContentCustom>
        {isLoading ? (
          <Spin />
        ) : (
          <>
            <DescriptionList data={infoBrokerFirm} translate />
            <DescriptionList data={infoOwner} translate />
            <DescriptionList data={infoGroup} translate />
          </>
        )}
      </ContentCustom>
      {user?.role === 'users:SUPER-USER' && (
        <>
          <PageHeader title={t('insurers.customportal.title')} />
          <ContentCustom>
            {user?.insurer && (
              <Row gutter={[0, 24]}>
                <Skeleton
                  loading={isLogoLoading}
                  title={false}
                  paragraph={false}
                  avatar={{ size: 64 }}
                >
                  <Popover
                    id="avatar-popover"
                    placement={isXSScreen ? 'topLeft' : 'right'}
                    title={
                      <>
                        <p>
                          <WarningOutlined
                            style={{ marginRight: 8, color: yellow.primary }}
                          />
                          {t('insurers.customportal.popconfirm.title')}
                        </p>
                        <p>{t('insurers.customportal.popconfirm.text')}</p>
                      </>
                    }
                  >
                    <div
                      style={{ position: 'relative', cursor: 'pointer' }}
                      onMouseEnter={() => setHover(true)}
                      onMouseLeave={() => setHover(false)}
                    >
                      <p className="avatar-edit-title">
                        {t('employees.avatar_edit')}
                      </p>
                      <Upload
                        name="avatar"
                        listType="picture-card"
                        className="avatar-uploader"
                        showUploadList={false}
                        beforeUpload={beforeUpload}
                      >
                        <>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                              width: '100%',
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              position: 'absolute',
                              zIndex: 2,
                              visibility: hover ? 'visible' : 'hidden'
                            }}
                          >
                            <EditOutlined
                              style={{ fontSize: 20, color: 'white' }}
                            />
                          </div>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt="avatar"
                              style={{ width: '100%' }}
                            />
                          ) : (
                            <Avatar size={40}>{user?.insurer?.name[0]}</Avatar>
                          )}
                        </>
                      </Upload>
                    </div>
                  </Popover>
                </Skeleton>
              </Row>
            )}
          </ContentCustom>
        </>
      )}
    </>
  );
};

export default MyBrokerFirm;
